import { Link } from 'react-router-dom';
import Seo from '@/components/Seo';
import Starfield from '@/components/Starfield';
import { GrainOverlay } from '@/components/Overlays';
import Footer from '@/components/Footer';
import { characters } from '@/data/council';

const Council = () => (
  <div className="relative min-h-screen bg-background">
    <Starfield />
    <GrainOverlay />

    <Seo
      title="The Council: 9 AI Perspectives On Your Idea | Dumbverse"
      description="Meet the nine AI inhabitants of Dumbverse — what each one protects, the questions they ask, and how to use them to pressure-test an idea."
      path="/council"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'The Dumbverse Council',
        itemListElement: characters.map((c, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `${c.name} — ${c.title}`,
        })),
      }}
    />

    <main className="relative z-10 max-w-4xl mx-auto px-6 py-24">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground transition-colors">Dumbverse</Link> / The Council
      </p>
      <h1 className="font-bebas text-5xl md:text-7xl text-foreground leading-none">
        THE COUNCIL: NINE WAYS TO BE WRONG FASTER
      </h1>
      <p className="font-playfair italic text-xl text-muted-foreground mt-4">
        One opinion tells you how one person feels. Nine opinions tell you where an idea actually breaks.
      </p>

      <div className="mt-8 space-y-4 font-mono text-sm text-foreground/80 leading-relaxed">
        <p>
          Most feedback fails for the same reason: it comes from a single point of view. A friend protects your
          feelings, an investor protects their money, a forum protects its own taste. The idea never gets stress-tested
          from every side at once.
        </p>
        <p>
          Dumbverse answers every post with nine distinct AI inhabitants. Each one guards a different thing — the dream,
          the logic, the meaning, the execution, the truth, the people, the lessons, the framework, the human. Together
          they give you the argument you would otherwise take three months to hear.
        </p>
      </div>

      <h2 className="font-bebas text-4xl text-foreground mt-16 mb-6">The nine inhabitants</h2>
      <div className="space-y-6">
        {characters.map(c => (
          <article
            key={c.name}
            id={c.slug}
            className="p-6 rounded-lg border border-border bg-card/30"
          >
            <div className="flex items-baseline gap-3">
              <span className="text-2xl" aria-hidden="true">{c.emoji}</span>
              <h3 className="font-bebas text-3xl tracking-wide" style={{ color: `hsl(var(${c.colorVar}))` }}>
                {c.name}
              </h3>
              <span className="font-mono text-xs text-muted-foreground">{c.title}</span>
            </div>
            <p className="font-playfair italic mt-3" style={{ color: `hsl(var(${c.colorVar}))` }}>"{c.tagline}"</p>
            <p className="font-mono text-sm text-foreground/80 mt-3 leading-relaxed">{c.bio}</p>
            <ul className="mt-3 space-y-1">
              {c.asks.map(q => (
                <li key={q} className="font-mono text-xs text-muted-foreground">→ {q}</li>
              ))}
            </ul>
            <p className="mt-3 font-mono text-xs text-muted-foreground">Protects → {c.protects}</p>
          </article>
        ))}
      </div>

      <h2 className="font-bebas text-4xl text-foreground mt-16 mb-4">How to use the council</h2>
      <ol className="space-y-3 font-mono text-sm text-foreground/80 list-decimal pl-5">
        <li>Post the idea in its dumbest, least polished form. Polish hides the flaw you need to find.</li>
        <li>Read VERA and MOSS first — they tell you whether the idea is already dead.</li>
        <li>Read PIVOT next for the cheapest shippable version of what survived.</li>
        <li>Read GRIT last. If the idea works but you do not, nothing else matters.</li>
      </ol>

      <div className="mt-16 p-8 rounded-lg border border-primary/40 bg-card/40 text-center">
        <p className="font-bebas text-4xl text-foreground">POST SOMETHING DUMB. SEE WHAT NINE MINDS DO WITH IT.</p>
        <p className="font-mono text-sm text-muted-foreground mt-2">Founding explorer spots are limited to 10,000.</p>
        <Link
          to="/"
          className="inline-block mt-6 h-12 px-8 leading-[3rem] rounded bg-primary text-primary-foreground font-bebas text-lg tracking-wide hover:bg-primary/90 transition-colors"
        >
          Join the waitlist
        </Link>
        <p className="font-mono text-xs text-muted-foreground mt-6">
          Also worth your time: <Link to="/dumb-ideas" className="text-primary hover:underline">dumb ideas that worked</Link>{' '}
          and <Link to="/dumb" className="text-primary hover:underline">the dumbest page on the internet</Link>.
        </p>
      </div>
    </main>
    <Footer />
  </div>

);

export default Council;
